import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { money, pct, relativeTime, formatApiError } from "../lib/api";
import PageHeader, { StatusPill } from "../components/app/PageHeader";
import { Plus, Search, FileText } from "lucide-react";

const STATUS_FILTERS = ["all", "draft", "sent", "won", "lost", "approved", "rejected"];

export default function QuotesList() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes", {
        params: { q: q || undefined, status: status !== "all" ? status : undefined, limit: 500 },
      });
      setQuotes(data);
    } catch (e) {
      console.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div data-testid="quotes-list-page">
      <PageHeader
        eyebrow="Pipeline"
        title="Quotes"
        subtitle="Search any quote — by number, title, or client."
        actions={
          <button onClick={() => nav("/quotes/new")} data-testid="quotes-new-btn" className="btn btn-primary">
            <Plus className="w-4 h-4" /> New Quote
          </button>
        }
      />

      <div className="p-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-3 w-4 h-4" style={{ color: "var(--text-faint)" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search quote # · title · client name…"
              data-testid="quotes-search-input"
              className="input pl-10"
            />
          </div>
          <div className="flex rounded-md overflow-hidden" style={{ border: "1px solid var(--border-strong)" }}>
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => { setStatus(s); setParams({ status: s }); }}
                data-testid={`filter-${s}`}
                className="px-3.5 py-2 text-[10px] font-bold tracking-widest uppercase transition-colors border-r last:border-r-0"
                style={{
                  borderColor: "var(--border-strong)",
                  background: status === s ? "var(--gradient-accent)" : "var(--surface-1)",
                  color: status === s ? "white" : "var(--text-muted)",
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="surface overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Title</th>
                <th>Client</th>
                <th>Status</th>
                <th className="text-right">Total</th>
                <th className="text-right">Margin</th>
                <th className="text-right">Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="text-center py-12 font-mono text-xs" style={{ color: "var(--text-muted)" }}>Loading…</td></tr>
              )}
              {!loading && quotes.length === 0 && (
                <tr><td colSpan="7" className="text-center py-20">
                  <div className="flex flex-col items-center gap-3">
                    <FileText className="w-8 h-8" style={{ color: "var(--text-faint)" }} />
                    <div className="text-sm" style={{ color: "var(--text-muted)" }}>No quotes match your filters.</div>
                  </div>
                </td></tr>
              )}
              {!loading && quotes.map((q) => (
                <tr key={q.id} className="clickable" onClick={() => nav(`/quotes/${q.id}`)} data-testid={`quote-row-${q.id}`}>
                  <td className="font-mono text-xs whitespace-nowrap" style={{ color: "var(--text-muted)" }}>
                    {q.quote_number}
                    {q.is_variation && (
                      <span className="ml-2 inline-block text-[10px] tracking-widest font-bold text-gradient">VAR</span>
                    )}
                  </td>
                  <td className="font-medium">{q.title || "—"}</td>
                  <td style={{ color: "var(--text-secondary)" }}>{q.client_name || "—"}</td>
                  <td><StatusPill status={q.status} /></td>
                  <td className="text-right font-mono" data-num>{money(q.total)}</td>
                  <td className="text-right font-mono" style={{ color: "var(--text-secondary)" }} data-num>{pct(q.margin_pct)}</td>
                  <td className="text-right text-xs font-mono whitespace-nowrap" style={{ color: "var(--text-faint)" }}>{relativeTime(q.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
